/**
 * Route Helper Utilities
 * 
 * Utility functions for route management and role-based navigation.
 */

import { USER_ROLES } from '../constants/roles';
import { getUserRole } from './roleHelpers';

/**
 * Get the default dashboard route for a user role
 * @param {string} role - User role
 * @returns {string} Dashboard route path
 */
export const getDashboardRoute = (role = null) => {
  const userRole = role || getUserRole();

  switch (userRole) {
    case USER_ROLES.ADMIN:
      return '/admin/default';
    case USER_ROLES.CENTER_ADMIN:
      return '/admin/center-admin';
    case USER_ROLES.BLOGGER:
      return '/admin/blogger';
    default:
      return '/auth/sign-in';
  }
};

/**
 * Get routes filtered by user role
 * @param {Array} routes - All routes
 * @param {string} role - User role
 * @returns {Array} Filtered routes
 */
export const getRoutesForRole = (routes, role = null) => {
  const userRole = role || getUserRole();

  if (!userRole) {
    return [];
  }

  return routes.reduce((filteredRoutes, route) => {
    // If route has no role restriction, show it
    let hasRouteAccess = true;

    if (route.allowedRoles) {
      // Check if user role is in allowed roles
      const allowedRoles = Array.isArray(route.allowedRoles)
        ? route.allowedRoles
        : [route.allowedRoles];

      hasRouteAccess = allowedRoles.includes(userRole);
    }

    if (!hasRouteAccess) {
      return filteredRoutes;
    }

    if ((route.category || route.collapse) && Array.isArray(route.items)) {
      const filteredItems = getRoutesForRole(route.items, userRole);
      if (filteredItems.length > 0) {
        filteredRoutes.push({
          ...route,
          items: filteredItems,
        });
      }
      return filteredRoutes;
    }

    filteredRoutes.push(route);
    return filteredRoutes;
  }, []);
};

/**
 * Check if a route is accessible by the current user
 * @param {Object} route - Route object
 * @param {string} role - User role
 * @returns {boolean} True if accessible
 */
export const isRouteAccessible = (route, role = null) => {
  const userRole = role || getUserRole();

  if (!route.allowedRoles) {
    return true;
  }

  const allowedRoles = Array.isArray(route.allowedRoles)
    ? route.allowedRoles
    : [route.allowedRoles];

  return allowedRoles.includes(userRole);
};

const routeHelpers = {
  getDashboardRoute,
  getRoutesForRole,
  isRouteAccessible,
};
export default routeHelpers;
